/**
 * Presentation of money, time and duration.
 *
 * Amounts arrive from the server as an integer number of rials and stay
 * integers all the way to the screen. Nothing here divides, and nothing here
 * produces a `number` that could later be added to another — formatting
 * returns a string precisely so a display helper can never become an
 * accidental arithmetic path.
 */

/** Persian digits, so «۲۵٬۰۰۰ ریال» rather than a Latin-numeral run in an RTL line. */
const RIAL = new Intl.NumberFormat('fa-IR', { useGrouping: true })

/**
 * A known rial amount.
 *
 * Deliberately strict, and deliberately NOT nullable. It throws on anything
 * that is not an integer because a fractional rial means an arithmetic bug
 * upstream, and rounding it away at the display boundary would hide the bug
 * while producing a number somebody might then reconcile against.
 *
 * Callers holding a value that may legitimately be absent must not call this.
 * Use `formatOptionalIrr`, or better the `<Money>` component, which makes the
 * absent case a deliberate decision rather than a crash.
 */
export function formatIrr(amountIrr: number): string {
  if (!Number.isInteger(amountIrr)) {
    throw new TypeError(`A rial amount must be an integer, received ${amountIrr}.`)
  }
  const sign = amountIrr < 0 ? '−' : ''
  return `${sign}${RIAL.format(Math.abs(amountIrr))} ریال`
}

/**
 * A rial amount that may not be configured yet.
 *
 * Most money on this platform starts unset: an operation has no sale price
 * until somebody sets one, a route has no provider cost until somebody enters
 * it. Null is what "nobody has decided this yet" looks like, and it is a
 * different fact from zero — zero is a decision to charge nothing.
 *
 * So there is no default placeholder. The caller says what absence means in
 * its own context, because «قیمت فروش تنظیم نشده» and «هزینه تأمین‌کننده
 * مشخص نشده» are different sentences and neither is «۰ ریال».
 */
export function formatOptionalIrr(
  amountIrr: number | null | undefined,
  whenUnknown: string,
): string {
  return amountIrr === null || amountIrr === undefined ? whenUnknown : formatIrr(amountIrr)
}

/** Common placeholders, so the same state is not worded three different ways. */
export const UNSET = {
  salePrice: 'قیمت فروش تنظیم نشده',
  providerCost: 'هزینه تأمین‌کننده مشخص نشده',
  publishedRange: 'بازه منتشرشده در دسترس نیست',
  profit: 'قابل محاسبه نیست',
  amount: '—',
  never: 'هنوز انجام نشده',
} as const

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return UNSET.amount
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return UNSET.amount
  // Gregorian, localised, rather than introducing a Jalali calendar casually:
  // this console reconciles against provider invoices and server logs, which
  // are Gregorian, and a second calendar is a translation step at exactly the
  // moment somebody is comparing two timestamps.
  //
  // calendar is stated rather than left to the locale. 'fa-IR' resolves to the
  // Persian calendar by default, so this printed Jalali dates under a comment
  // promising Gregorian ones — the portal said ۱۴۰۵/۰۶/۰۷ where an invoice and
  // the server log both said 2026-08-29.
  return parsed.toLocaleString('fa-IR', {
    calendar: 'gregory',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDuration(ms: number | null | undefined): string {
  return ms === null || ms === undefined ? UNSET.amount : `${RIAL.format(ms)} میلی‌ثانیه`
}

/** A whole-number count, in Persian digits. */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? UNSET.amount : RIAL.format(value)
}
